import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { observer } from 'mobx-react-lite';

import { Checkbox, HelperText, TableCell, TableRow } from '@ifs-gcp/components';
import type { IScopeNode, PackagingScopes, ValidationError } from '@ifs-gcp/core';
import type {
  FirstLevelPackagingPickerRowChild,
  Language,
  PackagingChild,
} from '@ifs-gcp/core/models';

import type { useTouchedField } from '@/store/validationStore';
import { getTranslation } from '@/utils/translations';

import styles from './packaging-scope-row.module.scss';

interface BaseScopeProps {
  language: Language;
  domain: PackagingScopes;
  errors?: ValidationError[];
  markTouched: ReturnType<typeof useTouchedField>['markTouched'];
}

interface PackagingScopeRowProps extends BaseScopeProps {
  template: IScopeNode<PackagingChild> | IScopeNode<FirstLevelPackagingPickerRowChild>;
}

const NestedScopeChildren = ({
  language,
  domain,
  template,
  errors,
  markTouched,
}: PackagingScopeRowProps) => {
  if (!template.children?.length) return null;

  return (
    <table>
      <tbody>
        {template.children.map((child) => (
          <TableRow key={child.uuid}>
            <PackagingScopeRow
              language={language}
              domain={domain}
              template={child}
              errors={errors}
              markTouched={markTouched}
            />
          </TableRow>
        ))}
      </tbody>
    </table>
  );
};

interface PackagingScopeTextRowProps extends BaseScopeProps {
  template: IScopeNode<FirstLevelPackagingPickerRowChild>;
}

const PackagingScopeTextRow = (props: PackagingScopeTextRowProps) => {
  const { language, template, errors } = props;

  const scopeError = errors?.find(({ id: errorId }) => errorId === template.uuid);

  return (
    <TableCell>
      <p>{getTranslation(template.text, language)}</p>

      {scopeError && (
        <HelperText error component="span">
          {getTranslation(scopeError.error, language)}
        </HelperText>
      )}

      <NestedScopeChildren {...props} />
    </TableCell>
  );
};

interface PackagingScopeProps extends BaseScopeProps {
  template: IScopeNode<PackagingChild>;
}

const PackagingScope = observer((props: PackagingScopeProps) => {
  const { domain, language, template, errors, markTouched } = props;

  const scopeUuid = domain.getScopeKey(template);
  const scope = domain.getScopeByUuid(scopeUuid);

  const scopeError = errors?.find(
    ({ id: errorId }) => errorId === `${template.productScope}_${template.uuid}`,
  );

  const handleChangeScope = () => {
    domain.changeScope(scopeUuid);

    markTouched();
  };

  return (
    <TableCell>
      <div className={styles.wrapper}>
        <Checkbox
          label={getTranslation(template.text, language)}
          disabled={scope.disabled}
          onChange={handleChangeScope}
          checked={scope.selected}
          data-testid={`packaging-scope-${scopeUuid}`}
        />

        {template.hintModalId && (
          <a href={`#modal=${template.hintModalId}`} className={styles.helpIcon}>
            <HelpOutlineOutlinedIcon />
          </a>
        )}

        {scopeError && (
          <HelperText error component="span">
            {getTranslation(scopeError.error, language)}
          </HelperText>
        )}
      </div>

      <NestedScopeChildren {...props} />
    </TableCell>
  );
});

const isSubProductScopeNode = (
  element: IScopeNode<PackagingChild> | IScopeNode<FirstLevelPackagingPickerRowChild>,
): element is IScopeNode<FirstLevelPackagingPickerRowChild> => {
  return element.type === 'subProductScope';
};

export const PackagingScopeRow = ({ template, ...rest }: PackagingScopeRowProps) => {
  if (isSubProductScopeNode(template)) {
    return <PackagingScopeTextRow {...rest} template={template} />;
  }

  return <PackagingScope {...rest} template={template} />;
};
